import React, { useState } from 'react';
import { useBranding } from '../../hooks/useBranding';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Link2, Loader2, Mail, CheckCircle2, X, MessageSquare, Smartphone, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  }
}
function emailToHtml(text) {
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const linkify = (line) => esc(line).replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" style="color:#15133A;font-weight:600;">$1</a>');
  return text.split('\n').map((l) => (l.trim() === '' ? '<div><br></div>' : `<div>${linkify(l)}</div>`)).join('');
}
async function copyRichEmail(text) {
  try {
    if (window.ClipboardItem && navigator.clipboard?.write) {
      const item = new window.ClipboardItem({
        'text/html': new Blob([emailToHtml(text)], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch { /* fall through */ }
  return copyText(text);
}
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', ' at'); } catch { return iso; } };
const firstName = (n) => (n || '').trim().split(' ')[0] || 'there';

const buildEmail = (signer, link, sender, biz = 'DerwentAI') =>
`Hi ${firstName(signer.name)},

I've just sent through a document that requires your signature.

You can review and securely sign the document using the link below:

${link}

Once completed, we'll automatically receive confirmation.

Thanks,
${sender || biz}
${biz}`;

const buildSms = (signer, link, sender, biz = 'DerwentAI') =>
`Hi ${firstName(signer.name)}, I've just sent you a document that needs your signature. You can review and sign it securely here:
${link}
Thanks,
${sender || biz} from ${biz}`;

// Post-send success screen — one permanent secure link per signer, SMS confirmation,
// and a ready-to-paste email template. Mirrors the Client Portal invitation UX.
export default function EsignSentPanel({ doc, smsResults, senderName, headers, onClose }) {
  const brand = useBranding();
  const recipients = (doc?.signers || []).filter((s) => !s.is_broker);
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState(null);
  const signer = recipients[idx];

  if (!signer) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4" data-testid="esign-sent-panel" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
          <p className="mt-3 text-lg font-black text-slate-900">Sent for signing</p>
          <p className="mt-1 text-sm text-slate-500">This request has no external signers to notify.</p>
          <button type="button" onClick={onClose} className="mt-5 rounded-full bg-[#15133A] px-6 py-2.5 text-sm font-bold text-white">Done</button>
        </div>
      </div>
    );
  }

  const origin = window.location.origin;
  const link = `${origin}/esign/sign/${signer.token}`;
  const email = buildEmail(signer, link, senderName, brand.business_name || 'DerwentAI');
  const sms = buildSms(signer, link, senderName, brand.business_name || 'DerwentAI');
  const smsRes = (smsResults || []).find((r) => r.signer_id === signer.id);
  const smsDelivered = smsRes ? smsRes.delivered : signer.sms_last_delivered;
  const smsDry = smsRes ? smsRes.dry_run : signer.sms_dry_run;
  const smsTo = (smsRes && smsRes.mobile) || signer.sms_to || signer.mobile;
  const smsAt = signer.sms_sent_at;

  const flash = (id, msg) => { setCopied(id); toast.success(msg); setTimeout(() => setCopied(null), 1800); };
  const logAction = (action) => { axios.post(`${API}/api/esign/documents/${doc.id}/log`, { action, signer_id: signer.id }, { headers }).catch(() => {}); };
  const copyEmail = async () => { if (await copyRichEmail(email)) { flash('email', 'Email copied — paste it into Outlook or Gmail (the link is a clickable hyperlink).'); logAction('email_copied'); } else toast.error('Copy failed — select and copy manually'); };
  const copyLink = async () => { if (await copyText(link)) { flash('link', 'Secure signing link copied.'); logAction('link_copied'); } };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-slate-950/50 backdrop-blur-sm p-0 sm:p-4" data-testid="esign-sent-panel" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex w-full flex-col sm:max-w-2xl max-h-[94vh] sm:max-h-[90vh] overflow-hidden rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="shrink-0 flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-5 sm:px-6 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> E-Sign request sent</p>
            <h2 className="text-lg sm:text-xl font-black text-slate-900 font-[Outfit] truncate" data-testid="esign-sent-title">{doc.title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{doc.customer_name || recipients.map((r) => r.name).join(' & ')} · Ref {doc.reference_number}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="esign-sent-close" className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>

        {/* Signer tabs (2+ applicants each get their own link + email) */}
        {recipients.length > 1 && (
          <div className="shrink-0 flex gap-2 overflow-x-auto border-b border-slate-100 px-5 sm:px-6 py-2.5" data-testid="esign-sent-signer-tabs">
            {recipients.map((r, i) => (
              <button key={r.id} type="button" onClick={() => setIdx(i)} data-testid={`esign-sent-tab-${i}`}
                      className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition ${i === idx ? 'bg-[#15133A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {r.name}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-4">
          {/* Recipient + SMS status */}
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="esign-sent-sms-status">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-black text-slate-900 font-[Outfit] truncate">{signer.name} <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 align-middle">{signer.role || 'Signer'}</span></p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                  {signer.email && (
                    <button type="button" onClick={async () => { (await copyText(signer.email)) ? toast.success('Email copied') : toast.error('Copy failed'); }} title="Click to copy email" data-testid="esign-sent-copy-email"
                            className="group/e inline-flex items-center gap-1 max-w-full text-slate-500 hover:text-slate-800 transition-colors">
                      <Mail className="w-3 h-3 shrink-0 text-slate-400" />
                      <span className="truncate underline decoration-dotted decoration-slate-300 underline-offset-2">{signer.email}</span>
                      <Copy className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover/e:opacity-100 transition-opacity" />
                    </button>
                  )}
                  {smsTo && (
                    <span className="inline-flex items-center gap-1">
                      <a href={`tel:${String(smsTo).replace(/[^+\d]/g, '')}`} title="Tap to call" data-testid="esign-sent-call"
                         className="text-slate-500 hover:text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2 transition-colors">{smsTo}</a>
                      <button type="button" onClick={async () => { (await copyText(String(smsTo))) ? toast.success('Phone number copied') : toast.error('Copy failed'); }} title="Copy number" data-testid="esign-sent-copy-phone"
                              className="text-slate-400 hover:text-slate-700 transition-colors"><Copy className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                </div>
              </div>
              {smsDelivered ? (
                <p className="text-sm font-black text-emerald-700 inline-flex items-center gap-1.5" data-testid="esign-sent-sms-sent"><CheckCircle2 className="w-4 h-4" /> SMS sent</p>
              ) : smsDry ? (
                <p className="text-sm font-bold text-amber-700 inline-flex items-center gap-1.5" data-testid="esign-sent-sms-dry"><Smartphone className="w-4 h-4" /> SMS queued (dry-run)</p>
              ) : (
                <p className="text-sm font-bold text-slate-500 inline-flex items-center gap-1.5" data-testid="esign-sent-sms-unknown"><MessageSquare className="w-4 h-4" /> SMS attempted</p>
              )}
            </div>
            <p className="mt-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
              {smsTo ? `Texted to ${smsTo}` : 'No mobile on file'}{smsAt ? ` · ${fmtDateTime(smsAt)}` : ''}{smsDry ? ' · add ClickSend keys to deliver live texts' : ''}
            </p>
          </div>

          {/* Secure link */}
          <div className="rounded-2xl border border-slate-200 bg-[#F7F7FB] px-4 py-3" data-testid="esign-sent-link-box">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 inline-flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Secure signing link <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /></p>
            <p className="mt-1 break-all text-xs font-semibold text-[#15133A]" data-testid="esign-sent-link">{link}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">Unique to {firstName(signer.name)} · stays the same until signed, cancelled or expired.</p>
          </div>

          {/* Email template */}
          <label className="block">
            <span className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Email template <span className="font-semibold normal-case tracking-normal text-slate-400">copy &amp; paste into Outlook / Gmail</span></span>
            <textarea value={email} readOnly rows={11} data-testid="esign-sent-email-body" className="mt-1 w-full min-h-[190px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-800 outline-none focus:border-[#15133A] resize-y" />
          </label>

          {/* SMS preview */}
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">SMS the customer received</span>
            <textarea value={sms} readOnly rows={4} data-testid="esign-sent-sms-body" className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600 outline-none resize-y" />
          </label>
        </div>

        {/* Footer actions */}
        <div className="shrink-0 border-t border-slate-100 bg-white px-5 sm:px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={copyEmail} data-testid="esign-sent-copy-email" className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#15133A] px-5 py-3 text-sm font-bold text-white hover:bg-[#1E1B52] min-h-[44px]">{copied === 'email' ? <CheckCircle2 className="w-4 h-4" /> : <Mail className="w-4 h-4" />} {copied === 'email' ? 'Copied!' : 'Copy Email'}</button>
            <button type="button" onClick={copyLink} data-testid="esign-sent-copy-link" className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:border-slate-900 min-h-[44px]">{copied === 'link' ? <CheckCircle2 className="w-4 h-4" /> : <Link2 className="w-4 h-4" />} Copy Link</button>
            <button type="button" onClick={onClose} data-testid="esign-sent-done" className="ml-auto inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-700 min-h-[44px]">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
